import { useEffect, useState } from 'react';

import { Info, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/usePageTitle';
import { billingApi } from '@/lib/api/billing.api';
import { formatDateTime } from '@/utils/format';

import type { MonthlyCostSummary } from '@repo/shared-types';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getDaysInMonth(month: string): number {
  const [year, monthNum] = month.split('-').map(Number);
  return new Date(year, monthNum, 0).getDate();
}

export const PlatformCostsPage = () => {
  usePageTitle('GCP Costs');
  const [searchParams, setSearchParams] = useSearchParams();
  const month = searchParams.get('month') || getCurrentMonth();
  const dayParam = searchParams.get('day');
  const day = dayParam ? parseInt(dayParam, 10) : undefined;
  const [data, setData] = useState<MonthlyCostSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (refresh = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await billingApi.getMonthlyCost(month, day, refresh);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError('Failed to load billing data. BigQuery may not be configured.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, day]);

  const handleMonthChange = (newMonth: string) => {
    const params: Record<string, string> = { month: newMonth };
    if (day) params.day = String(day);
    setSearchParams(params);
  };

  const handleDayChange = (newDay: string) => {
    const params: Record<string, string> = { month };
    if (newDay) params.day = newDay;
    setSearchParams(params);
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const serviceCount = data?.services.length ?? 0;
  const daysInMonth = getDaysInMonth(month);

  return (
    <div className="space-y-10">
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Infrastructure</p>
        <h1 className="text-4xl font-bold tracking-tight">GCP Costs</h1>
        <p className="text-muted-foreground max-w-3xl">
          GCP billing breakdown by service. Data is cached for 1 hour.
        </p>
        <div className="bg-muted/50 flex items-start gap-3 rounded-lg border p-4">
          <Info className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          <div className="text-muted-foreground text-sm">
            <p className="font-medium">Data availability notice</p>
            <p className="mt-1">
              Billing data may take 24-48 hours to appear after usage. Historical data before
              February 2026 (when BigQuery export was enabled) is incomplete or unavailable.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div>
          <label htmlFor="month-picker" className="text-sm font-medium">
            Month
          </label>
          <input
            id="month-picker"
            type="month"
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            max={getCurrentMonth()}
            className="border-input bg-background ring-offset-background focus-visible:ring-ring ml-3 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          />
        </div>
        <div>
          <label htmlFor="day-picker" className="text-sm font-medium">
            Day
          </label>
          <select
            id="day-picker"
            value={day || ''}
            onChange={(e) => handleDayChange(e.target.value)}
            className="border-input bg-background ring-offset-background focus-visible:ring-ring ml-3 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <option value="">All month</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      ) : !data || data.services.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No billing data available for {month}
              {day ? ` day ${day}` : ''}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>Total Cost</CardDescription>
                <CardTitle className="text-3xl">{formatCurrency(data.totalCost)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  {data.currency} &middot; {month}
                  {day ? ` day ${day}` : ''}
                </p>
              </CardContent>
            </Card>
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>Services</CardDescription>
                <CardTitle className="text-3xl">{serviceCount}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Active GCP services</p>
              </CardContent>
            </Card>
            {data.cachedAt && (
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardDescription>Last Updated</CardDescription>
                  <CardTitle className="text-lg">{formatDateTime(data.cachedAt)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Cached for 1 hour</p>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Service Breakdown</CardTitle>
              <CardDescription>Cost per GCP service, sorted by spend</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-3 text-left font-medium">Service Name</th>
                      <th className="pb-3 text-right font-medium">Cost</th>
                      <th className="pb-3 text-right font-medium">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.services.map((service) => {
                      const percentage =
                        data.totalCost > 0
                          ? ((service.cost / data.totalCost) * 100).toFixed(1)
                          : '0.0';
                      return (
                        <tr key={service.serviceName} className="border-b last:border-0">
                          <td className="py-3">{service.serviceName}</td>
                          <td className="py-3 text-right font-mono">
                            {formatCurrency(service.cost)}
                          </td>
                          <td className="py-3 text-right">
                            <span className="text-muted-foreground">{percentage}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-semibold">
                      <td className="pt-3">Total</td>
                      <td className="pt-3 text-right font-mono">
                        {formatCurrency(data.totalCost)}
                      </td>
                      <td className="pt-3 text-right">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
